// @ts-nocheck
/**
 * TourGuide, a lightweight guided tour system.
 *
 * No external dependencies. Uses getBoundingClientRect() to position a
 * spotlight cutout and a floating tooltip beside the highlighted element.
 *
 * Usage:
 *   <TourGuide steps={TOUR_STEPS} onClose={() => setShowTour(false)} />
 *
 * Each step: { selector, title, description, placement? }
 *   placement: 'right' | 'left' | 'bottom' | 'top' (auto-detected if omitted)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// ── Tour step definitions ──────────────────────────────────────────────────
// `title`/`description` are resolved from the catalog at render time inside
// the component below (no caller passes a custom `steps` prop today); only
// the selector/placement stay fixed here.

export const TOUR_STEP_META = [
  { key: 'navigation', selector: '[data-tour="sidebar"]', placement: 'right' },
  { key: 'contactLists', selector: '[data-tour="source-tabs"]', placement: 'bottom' },
  { key: 'startCalling', selector: '[data-tour="start-calling"]', placement: 'bottom' },
  { key: 'prospectQueue', selector: '[data-tour="prospect-table"]', placement: 'top' },
  { key: 'analytics', selector: '[data-tour="analytics-nav"]', placement: 'right' },
  { key: 'aiAssistant', selector: '[data-tour="ai-nav"]', placement: 'right' },
  { key: 'settings', selector: '[data-tour="settings-nav"]', placement: 'right' },
];

// ── Utility helpers ────────────────────────────────────────────────────────

const PAD = 8; // spotlight padding around element
const TOOLTIP_W = 320;
const TOOLTIP_H_ESTIMATE = 180;
const ARROW_SIZE = 10;

function getRect(selector) {
  try {
    const el = document.querySelector(selector);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return r;
  } catch {
    return null;
  }
}

function computeTooltipPosition(rect, placement) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spotLeft = rect.left - PAD;
  const spotTop = rect.top - PAD;
  const spotRight = rect.right + PAD;
  const spotBottom = rect.bottom + PAD;
  const spotCX = (spotLeft + spotRight) / 2;
  const spotCY = (spotTop + spotBottom) / 2;

  // Auto-detect best placement if not specified
  let p = placement;
  if (!p) {
    const spaceRight = vw - spotRight;
    const spaceLeft = spotLeft;
    const spaceBottom = vh - spotBottom;
    const spaceTop = spotTop;
    if (spaceRight >= TOOLTIP_W + 24) p = 'right';
    else if (spaceLeft >= TOOLTIP_W + 24) p = 'left';
    else if (spaceBottom >= TOOLTIP_H_ESTIMATE + 24) p = 'bottom';
    else p = 'top';
  }

  let style = {};
  let arrowStyle = {};
  let resolvedPlacement = p;

  switch (p) {
    case 'right':
      style = {
        left: Math.min(spotRight + ARROW_SIZE + 4, vw - TOOLTIP_W - 8),
        top: Math.max(8, Math.min(spotCY - TOOLTIP_H_ESTIMATE / 2, vh - TOOLTIP_H_ESTIMATE - 8)),
        width: TOOLTIP_W,
      };
      arrowStyle = {
        left: -ARROW_SIZE,
        top: '50%',
        transform: 'translateY(-50%)',
        borderTop: `${ARROW_SIZE}px solid transparent`,
        borderBottom: `${ARROW_SIZE}px solid transparent`,
        borderRight: `${ARROW_SIZE}px solid hsl(var(--card))`,
      };
      break;
    case 'left':
      style = {
        left: Math.max(8, spotLeft - ARROW_SIZE - 4 - TOOLTIP_W),
        top: Math.max(8, Math.min(spotCY - TOOLTIP_H_ESTIMATE / 2, vh - TOOLTIP_H_ESTIMATE - 8)),
        width: TOOLTIP_W,
      };
      arrowStyle = {
        right: -ARROW_SIZE,
        top: '50%',
        transform: 'translateY(-50%)',
        borderTop: `${ARROW_SIZE}px solid transparent`,
        borderBottom: `${ARROW_SIZE}px solid transparent`,
        borderLeft: `${ARROW_SIZE}px solid hsl(var(--card))`,
      };
      break;
    case 'bottom':
      style = {
        left: Math.max(8, Math.min(spotCX - TOOLTIP_W / 2, vw - TOOLTIP_W - 8)),
        top: Math.min(spotBottom + ARROW_SIZE + 4, vh - TOOLTIP_H_ESTIMATE - 8),
        width: TOOLTIP_W,
      };
      arrowStyle = {
        top: -ARROW_SIZE,
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: `${ARROW_SIZE}px solid transparent`,
        borderRight: `${ARROW_SIZE}px solid transparent`,
        borderBottom: `${ARROW_SIZE}px solid hsl(var(--card))`,
      };
      break;
    case 'top':
    default:
      style = {
        left: Math.max(8, Math.min(spotCX - TOOLTIP_W / 2, vw - TOOLTIP_W - 8)),
        top: Math.max(8, spotTop - ARROW_SIZE - 4 - TOOLTIP_H_ESTIMATE),
        width: TOOLTIP_W,
      };
      arrowStyle = {
        bottom: -ARROW_SIZE,
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: `${ARROW_SIZE}px solid transparent`,
        borderRight: `${ARROW_SIZE}px solid transparent`,
        borderTop: `${ARROW_SIZE}px solid hsl(var(--card))`,
      };
      resolvedPlacement = 'top';
      break;
  }

  return { tooltipStyle: style, arrowStyle, resolvedPlacement };
}

// ── Main component ─────────────────────────────────────────────────────────

export default function TourGuide({ steps, onClose }) {
  const { t } = useTranslation('dialer');
  const resolvedSteps = steps || TOUR_STEP_META.map((s) => ({
    ...s,
    title: t(`dialer:tourGuide.steps.${s.key}.title`),
    description: t(`dialer:tourGuide.steps.${s.key}.description`),
  }));
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const rafRef = useRef(null);

  const step = resolvedSteps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === resolvedSteps.length - 1;

  // Measure target element, retry until found (DOM may need a frame to settle)
  const measureTarget = useCallback(() => {
    if (!step) return;
    const r = getRect(step.selector);
    if (r) {
      setRect(r);
      setVisible(true);
    } else {
      // Element not found — keep stepping forward to avoid blocking
      setRect(null);
      setVisible(true);
    }
  }, [step]);

  useEffect(() => {
    setVisible(false);
    setRect(null);
    // One RAF to let React render the new step label, then measure
    rafRef.current = requestAnimationFrame(() => {
      measureTarget();
    });
    return () => cancelAnimationFrame(rafRef.current);
  }, [stepIndex, measureTarget]);

  // Recompute on scroll/resize
  useEffect(() => {
    const refresh = () => measureTarget();
    window.addEventListener('resize', refresh, { passive: true });
    window.addEventListener('scroll', refresh, { passive: true, capture: true });
    return () => {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('scroll', refresh, { capture: true });
    };
  }, [measureTarget]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (!isLast) setStepIndex(i => i + 1);
        else onClose();
        return;
      }
      if (e.key === 'ArrowLeft' && !isFirst) {
        setStepIndex(i => i - 1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFirst, isLast, onClose]);

  if (!step) return null;

  // Spotlight geometry
  const spotLeft = rect ? rect.left - PAD : -9999;
  const spotTop = rect ? rect.top - PAD : -9999;
  const spotW = rect ? rect.width + PAD * 2 : 0;
  const spotH = rect ? rect.height + PAD * 2 : 0;

  const { tooltipStyle, arrowStyle } = rect
    ? computeTooltipPosition(rect, step.placement)
    : { tooltipStyle: { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: TOOLTIP_W }, arrowStyle: {} };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9000,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.2s ease',
      }}
    >
      {/* ── Dark overlay with SVG cutout ── */}
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'auto' }}
        onClick={onClose}
      >
        <defs>
          <mask id="tour-mask">
            {/* White = show overlay; black = transparent (spotlight) */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={spotLeft}
                y={spotTop}
                width={spotW}
                height={spotH}
                rx={8}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.55)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* ── Spotlight border ring ── */}
      {rect && (
        <div
          style={{
            position: 'absolute',
            left: spotLeft,
            top: spotTop,
            width: spotW,
            height: spotH,
            borderRadius: 8,
            border: '2px solid hsl(var(--primary))',
            boxShadow: '0 0 0 1px hsl(var(--primary) / 0.2), 0 0 20px hsl(var(--primary) / 0.15)',
            pointerEvents: 'none',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      )}

      {/* ── Tooltip card ── */}
      <div
        style={{
          position: 'absolute',
          ...tooltipStyle,
          pointerEvents: 'auto',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Arrow */}
        <div
          style={{
            position: 'absolute',
            width: 0,
            height: 0,
            ...arrowStyle,
          }}
        />

        {/* Card */}
        <div
          style={{
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 16px 10px',
              borderBottom: '1px solid hsl(var(--border))',
              background: 'hsl(var(--primary) / 0.04)',
            }}
          >
            <HelpCircle
              style={{ width: 16, height: 16, color: 'hsl(var(--primary))', flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: 'hsl(var(--foreground))',
                flex: 1,
              }}
            >
              {step.title}
            </span>
            {/* Step counter */}
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: 'hsl(var(--muted-foreground))',
                background: 'hsl(var(--secondary))',
                borderRadius: 20,
                padding: '1px 7px',
              }}
            >
              {stepIndex + 1} / {resolvedSteps.length}
            </span>
            {/* Close */}
            <button
              onClick={onClose}
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'hsl(var(--muted-foreground))',
                flexShrink: 0,
              }}
              title={t('dialer:tourGuide.closeEscape')}
            >
              <X style={{ width: 14, height: 14 }} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '12px 16px 14px' }}>
            <p
              style={{
                fontSize: 13,
                lineHeight: 1.55,
                color: 'hsl(var(--muted-foreground))',
                margin: 0,
              }}
            >
              {step.description}
            </p>
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px 14px',
              gap: 8,
            }}
          >
            {/* Dot indicators */}
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {resolvedSteps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStepIndex(i)}
                  style={{
                    width: i === stepIndex ? 16 : 6,
                    height: 6,
                    borderRadius: 3,
                    border: 'none',
                    cursor: 'pointer',
                    background:
                      i === stepIndex
                        ? 'hsl(var(--primary))'
                        : 'hsl(var(--border))',
                    transition: 'all 0.2s ease',
                    padding: 0,
                  }}
                  title={t('dialer:tourGuide.stepAria', { number: i + 1 })}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div style={{ display: 'flex', gap: 6 }}>
              {!isFirst && (
                <button
                  onClick={() => setStepIndex(i => i - 1)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '6px 12px',
                    borderRadius: 7,
                    border: '1px solid hsl(var(--border))',
                    background: 'hsl(var(--secondary))',
                    color: 'hsl(var(--foreground))',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <ChevronLeft style={{ width: 13, height: 13 }} />
                  {t('dialer:tourGuide.back')}
                </button>
              )}
              <button
                onClick={() => {
                  if (isLast) onClose();
                  else setStepIndex(i => i + 1);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '6px 14px',
                  borderRadius: 7,
                  border: 'none',
                  background: 'hsl(var(--primary))',
                  color: 'hsl(var(--primary-foreground))',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {isLast ? t('dialer:tourGuide.done') : t('dialer:tourGuide.next')}
                {!isLast && <ChevronRight style={{ width: 13, height: 13 }} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
